import { Icon } from "@chakra-ui/react";
import { MdBarChart } from "react-icons/md";
import MiniStatistics from "../../../components/card/MiniStatistics";
import IconBox from "../../../components/icons/IconBox";
import { translate } from "../../../i18n";
import { useSelectedLocation } from "../../location-management/hooks/useSelectedLocation";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import { getDemoGameHours, getDemoGrowth, isDemoOrganization } from "../demoAnalyticsData";
import { AnalyticsFilterParams } from "../hooks/analyticsFilterParams";
import { useGetAllCourtsUsage } from "../hooks/useGetAllCourtsUsage";
import { computeGrowth } from "../utils/computeGrowth";
import { UNKNOWN_STAT_VALUE, isUnreadable, statPlaceholder } from "../utils/statValue";

type Props = {
    boxColor: string;
    brandColor: string;
    startDate: Date;
    endDate: Date;
    previousStartDate: Date | null;
    previousEndDate: Date | null;
    growthLabel?: string;
    filters?: AnalyticsFilterParams;
};
export default function TotalGameHoursCard({ boxColor, brandColor, startDate, endDate, previousStartDate, previousEndDate, growthLabel, filters }: Props) {
    const { selectedLocation } = useSelectedLocation();
    const { selectedOrganization } = useSelectedOrganization();
    const isDemo = isDemoOrganization(selectedOrganization?.id);
    const locationId = selectedLocation?.id;
    /**
     * `GET analytics/all-courts-usage` carries `@UseGuards(AdminAuthGuard)` —
     * read from `api/src/analytics/analytics.controller.ts` this run — so a
     * stale token or a revoked admin is refused with no outage at all, on top
     * of the ordinary 5xx/network paths.
     */
    const { data, isInitialLoading: isPending, isError } = useGetAllCourtsUsage(locationId, startDate, endDate, filters);
    // Comparison read: feeds `computeGrowth` only, and `previousHours` is
    // already `undefined` when the read has nothing — which is what makes the
    // guard effective. Allowlisted in `analytics-read-error-gate`.
    const { data: previousData } = useGetAllCourtsUsage(
        locationId,
        previousStartDate ?? startDate,
        previousEndDate ?? endDate,
        filters,
    );

    const orgId = selectedOrganization?.id;
    const unreadable = isUnreadable(isError, data);
    const demoTotal = isDemo ? getDemoGameHours(undefined, undefined, orgId) : 0;
    const demoPeriod = isDemo ? getDemoGameHours(startDate, endDate, orgId) : 0;
    const totalMs = data?.reduce((acc, curr) => acc + curr.totalGameDurationsInMs, 0);
    const totalHours = isDemo ? demoTotal : totalMs != null ? totalMs / 3_600_000 : undefined;
    const partialHours = totalHours;
    const prevMs = previousData?.reduce((acc, curr) => acc + curr.totalGameDurationsInMs, 0);
    const previousHours = prevMs != null ? prevMs / 3_600_000 : undefined;

    /**
     * `!== undefined`, not a truthiness test, and a placeholder rather than a
     * literal `'0'` in the last arm.
     *
     * This read is gated on `!!locationId`; `/analytics` renders the card with
     * or without a selected location, and a DISABLED query is reported by React
     * Query v4 as `isLoading: true` with `fetchStatus: 'idle'`, so the card sat
     * on `--` forever (R100-1). A venue that genuinely played NO hours must
     * still say `0`, and `0` is falsy — hence the explicit undefined test.
     */
    const totalValue = !isDemo && unreadable ? UNKNOWN_STAT_VALUE : totalHours !== undefined ? totalHours : statPlaceholder(isPending);
    const partialValue = !isDemo && unreadable ? UNKNOWN_STAT_VALUE : partialHours !== undefined ? partialHours : statPlaceholder(isPending);
    const growth = isDemo
        ? getDemoGrowth(demoPeriod, previousStartDate, previousEndDate, getDemoGameHours, orgId)
        : previousStartDate ? computeGrowth(partialHours, previousHours) : undefined;

    return (
        <MiniStatistics
            startContent={
                <IconBox
                    w='56px'
                    h='56px'
                    bg={boxColor}
                    icon={<Icon w='32px' h='32px' as={MdBarChart} color={brandColor} />}
                />
            }
            name={translate('analytics.game_hours')}
            value={`${partialValue} / ${totalValue}`}
            isError={!isDemo && unreadable}
            growth={growth}
            growthLabel={growthLabel}
        />)
}
